import React, { useState } from 'react';
import { Upload, CheckCircle, ArrowRight, Send, Loader2, Calendar } from 'lucide-react';
import axios from 'axios';

const LeadForm = ({ service }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    appointmentDate: '',
    appointmentTime: '',
    privacy: false
  });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const API_KEY = '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';

    try {
      const contactData = {
        email: formData.email,
        attributes: {
          NOME: formData.firstName,
          COGNOME: formData.lastName,
          TELEFONO: formData.phone
        },
        updateEnabled: true,
        listIds: [2] 
      };

      try {
        await axios.post('https://api.brevo.com/v3/contacts', contactData, {
          headers: {
            'api-key': API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
      } catch (contactError) {
        console.warn('Contact creation warning:', contactError);
      }

      let emailData = {
        sender: { name: "Preventivi Saving Solutions", email: "info@savingsolutionsofficial.com" },
        to: [{ email: "info@savingsolutionsofficial.com", name: "Admin" }],
        subject: `Nuova richiesta preventivo: ${service} - ${formData.firstName} ${formData.lastName}`,
        htmlContent: `
          <div style="font-family: Arial, sans-serif; color: #333;">
            <h1 style="color: #011D58;">Nuova Richiesta Preventivo</h1>
            <p><strong>Servizio:</strong> ${service}</p>
            <hr />
            <h3>Dati Cliente</h3>
            <p><strong>Nome:</strong> ${formData.firstName}</p>
            <p><strong>Cognome:</strong> ${formData.lastName}</p>
            <p><strong>Telefono:</strong> ${formData.phone}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <hr />
            <h3>Richiesta Appuntamento</h3>
            <p><strong>Data:</strong> ${formData.appointmentDate || 'Non specificata'}</p>
            <p><strong>Orario preferito:</strong> ${formData.appointmentTime || 'Non specificato'}</p>
            <hr />
            <p><em>Il contatto è stato elaborato e salvato su Brevo.</em></p>
          </div>
        `
      };

      if (file) {
        const base64Content = await convertFileToBase64(file);
        const content = base64Content.split(',')[1];
        
        emailData.attachment = [{
          content: content,
          name: file.name
        }];
      }

      await axios.post('https://api.brevo.com/v3/smtp/email', emailData, {
        headers: {
          'api-key': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      setSuccess(true);
      alert('Richiesta inviata con successo! Ti ricontatteremo presto.');
      
      setStep(1);
      setFile(null);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        appointmentDate: '',
        appointmentTime: '',
        privacy: false
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Si è verificato un errore durante l\'invio. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  return (
    <div className="bg-brand-secondary rounded-2xl shadow-xl p-6 md:p-8 max-w-2xl mx-auto mt-8 border border-brand-primary">
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-white mb-2">
          Richiedi preventivo per <span className="text-brand-accent underline decoration-brand-accent decoration-4 underline-offset-2">{service}</span>
        </h3>
        <p className="text-brand-muted">
          Compila il form per ricevere la migliore offerta personalizzata.
        </p>
      </div>

      <div className="flex items-center mb-8">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-brand-accent text-brand-primary' : 'bg-brand-primary text-brand-muted'} font-bold transition-colors duration-300`}>1</div>
        <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-brand-accent' : 'bg-brand-primary'} transition-colors duration-300`}></div>
        <div className={`flex-center flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-brand-accent text-brand-primary' : 'bg-brand-primary text-brand-muted'} font-bold transition-colors duration-300`}>2</div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/50 text-red-200 rounded-lg border border-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="space-y-6 animate-fade-in-up">
            <div className="border-2 border-dashed border-brand-primary rounded-xl p-8 text-center hover:border-brand-accent transition-colors cursor-pointer bg-brand-primary/20 hover:bg-brand-primary/40 group">
              <input 
                type="file" 
                id="file-upload" 
                className="hidden" 
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <label htmlFor="file-upload" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                <div className="bg-brand-primary p-4 rounded-full shadow-sm mb-4 group-hover:scale-110 transition-transform">
                  {file ? <CheckCircle className="w-8 h-8 text-brand-accent" /> : <Upload className="w-8 h-8 text-brand-accent" />}
                </div>
                <p className="text-lg font-medium text-white mb-1">
                  {file ? file.name : (service === 'Noleggio Auto' ? 'Carica il tuo preventivo attuale' : 'Carica la tua bolletta o contratto attuale')}
                </p>
                <p className="text-sm text-brand-muted">
                  {file ? 'File caricato con successo' : `Clicca o trascina qui ${service === 'Noleggio Auto' ? 'il preventivo' : 'la bolletta'} (PDF, JPG)`}
                </p>
              </label>
            </div>

            <div className="text-center">
              <p className="text-sm text-brand-muted mb-4">Non hai il documento a portata di mano? Puoi procedere comunque.</p>
              <button 
                type="button" 
                onClick={nextStep}
                className="bg-brand-accent text-brand-primary px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-brand-primary transition-all transform hover:scale-105 flex items-center justify-center mx-auto gap-2 shadow-lg shadow-brand-accent/20"
              >
                Continua <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-muted mb-1">Nome</label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-brand-primary bg-brand-primary/30 text-white rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all placeholder-brand-muted/50"
                  placeholder="Mario"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-muted mb-1">Cognome</label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-brand-primary bg-brand-primary/30 text-white rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all placeholder-brand-muted/50"
                  placeholder="Rossi"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-muted mb-1">Telefono</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-brand-primary bg-brand-primary/30 text-white rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all placeholder-brand-muted/50"
                  placeholder="333 1234567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-muted mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-brand-primary bg-brand-primary/30 text-white rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all placeholder-brand-muted/50"
                  placeholder="mario.rossi@email.it"
                />
              </div>
            </div>

            <div className="border-t border-brand-primary my-4 pt-4">
              <h4 className="text-md font-semibold text-brand-accent mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Richiedi Appuntamento (Opzionale)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-muted mb-1">Data</label>
                  <input
                    type="date"
                    name="appointmentDate"
                    value={formData.appointmentDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-brand-primary bg-brand-primary/30 text-white rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-muted mb-1">Orario Preferito</label>
                  <input
                    type="time"
                    name="appointmentTime"
                    value={formData.appointmentTime}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-brand-primary bg-brand-primary/30 text-white rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all [color-scheme:dark]"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 mt-6">
              <input
                type="checkbox"
                name="privacy"
                id="privacy"
                required
                checked={formData.privacy}
                onChange={handleInputChange}
                className="mt-1 h-4 w-4 min-w-4 text-brand-accent rounded border-brand-primary focus:ring-brand-accent bg-brand-primary/30 cursor-pointer"
              />
              <label htmlFor="privacy" className="text-sm text-brand-muted cursor-pointer">
                Ho letto l'informativa e acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR). <a href="/privacy" target="_blank" className="text-brand-accent underline hover:text-white">Leggi informativa</a>
              </label>
            </div>

            <div className="flex gap-4 mt-8">
              <button 
                type="button" 
                onClick={prevStep}
                disabled={loading}
                className="flex-1 bg-brand-primary text-brand-muted px-6 py-3 rounded-lg font-semibold hover:bg-brand-primary/80 hover:text-white transition-colors disabled:opacity-50 border border-brand-primary"
              >
                Indietro
              </button>
              <button 
                type="submit" 
                disabled={loading}
                className="flex-1 bg-brand-accent text-brand-primary px-6 py-3 rounded-lg font-bold hover:bg-white hover:text-brand-primary transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg shadow-brand-accent/20 disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Invio in corso...
                  </>
                ) : (
                  <>
                    Richiedi Preventivo <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default LeadForm;
